import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { DrawerProps, Tooltip } from 'antd';
import React, { FC, SVGProps, useState } from 'react';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { CloseOutlined } from '@ant-design/icons';
import { CohortType, CreateCohortType } from '@dynamika-shared/types/cohorts';
import { useForm } from 'react-hook-form';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Switch } from '@dynamika-shared/ui/Switch/Switch';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Attention as AttentionIcon } from '@dynamika-shared/ui/icons';
import { useParams } from 'react-router-dom';
import { CohortMode } from './ClinicalTrialVisits';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';

type CreateCohortProps = DrawerProps & {
  close?: () => void;
  cohortMode: CohortMode;
  numberOfPatient?: number;
  cohort?: CohortType;
};

interface HelperProps {
  icon: FC<SVGProps<SVGSVGElement>>;
}
export const Helper: FC<HelperProps> = ({ icon }) => {
  const Icon = icon;
  const tooltipTitle = (
    <div className='text-chineseBlack'>
      <div>% - Number</div>
      <div>* - Letters</div>
      <div className='mt-2 text-spanishGray'>Example</div>
      <div>KL_*%%-*%%=KL_H99-E88</div>
    </div>
  );
  return (
    <Tooltip color='#F0F5FF' title={tooltipTitle}>
      <Icon
        className='ml-[.5rem] cursor-pointer hover:text-dodgerBlue'
        data-testid='subjectCodeTooltip'
      />
    </Tooltip>
  );
};

//  Allowed integer only
export const numericInputPattern = /^\d*$/;

const patientCodePattern = /^(?=.*[%*#])[a-zA-Z0-9%*#_-]*$/;

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  patientCodePattern: yup
    .string()
    .required('Subject Code Pattern is required')
    .matches(
      patientCodePattern,
      'Subject Code Pattern should contain alphanumerical with placeholders'
    )
    .max(50, 'Subject Code Pattern should not exceed 50 characters'),
  unscheduledAllowed: yup.boolean().required('Unscheduled Allowed is required'),
  fetchPatientGender: yup
    .boolean()
    .required('Subject Gender Selection is required'),
  numberOfPatients: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === '' ? null : value;
    })
    .required('Number of Subjects is required')
    .min(1, 'Number of  Subjects must be at least 1')
    .max(99999, 'Number of  Subjects must be at most 99999'),
});

export const CreateCohort: FC<CreateCohortProps> = ({
  close,
  cohortMode,
  numberOfPatient,
  cohort,
}) => {
  const { id: clinicalTrialId } = useParams<{
    id: string;
  }>();
  const [askQuit, setAskQuit] = useState<boolean>(false);
  const [loading, createCohort, updateCohort] = useClinicalTrialSlice(
    (state, actions) => [
      state.clinicalTrialActionLoading,
      actions.createCohortRequest,
      actions.updateCohortRequest,
    ]
  );

  const defaultValues: CreateCohortType =
    cohort && cohortMode === 'EDIT_COHORT'
      ? {
          name: cohort.name,
          patientCodePattern: cohort.patientCodePattern,
          numberOfPatients:
            cohort.numberOfPatients?.toString() as unknown as number,
          unscheduledAllowed: cohort.unscheduledAllowed,
          fetchPatientGender: cohort.fetchPatientGender,
        }
      : {
          name: cohortMode === 'NO_COHORT' ? 'Default' : '',
          patientCodePattern: '',
          numberOfPatients: (cohortMode === 'NO_COHORT'
            ? (numberOfPatient?.toString() ?? '')
            : '') as unknown as number,
          unscheduledAllowed: true,
          fetchPatientGender: false,
        };

  const { setValue, getValues, trigger, watch, formState } =
    useForm<CreateCohortType>({
      resolver: yupResolver(schema),
      defaultValues,
    });
  const { errors, isValid, isSubmitting, isDirty, touchedFields } = formState;

  const onChange = async (
    fieldName: keyof CreateCohortType,
    value: string | boolean
  ) => {
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    trigger(fieldName);
  };

  const onBlur = (fieldName: keyof CreateCohortType, value: string) => {
    if (touchedFields[fieldName]) {
      onChange(fieldName, value.trim());
    }
  };

  const onKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    value: string
  ) => {
    //  "0" is not allowed as first character.
    if (!numericInputPattern.test(e.key ?? '') || (!value && e.key === '0')) {
      e.preventDefault();
    }
  };

  const submit = async () => {
    if (clinicalTrialId) {
      createCohort({
        clinicalTrialId,
        data: getValues(),
      });
    }
  };

  const update = async () => {
    if (clinicalTrialId && cohort?.id) {
      updateCohort({
        clinicalTrialId,
        cohortId: cohort.id,
        data: getValues(),
      });
    }
  };

  const onCloseDrawer = () => {
    if (!isDirty) {
      close && close();
    } else {
      setAskQuit(true);
    }
  };

  return (
    <Drawer
      classNames={{
        header:
          '!px-0 !pb-3 !pt-1 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !px-0 !pb-0',
        content: 'rounded-lg',
      }}
      title={
        cohort ? (
          <div className='flex gap-1'>
            <span className='pt-[0.25rem]'>Update</span>
            <CustomTooltip MAX_LABEL_LENGTH={10} label={cohort?.name} />
            <span className='pt-[0.2rem]'>cohort</span>
          </div>
        ) : (
          'Add Cohort'
        )
      }
      open
      width={464}
      placement='right'
      onClose={onCloseDrawer}
      closeIcon={<CloseOutlined />}
    >
      <form className='overflow-auto px-1'>
        <Input
          required
          value={watch('name')}
          name={'name'}
          data-testid={'name'}
          onChange={e => onChange('name', e.target.value)}
          onBlur={e => onBlur('name', e.target.value)}
          errors={errors.name?.message}
          label={'Name'}
          placeholder={'Name'}
          classNameInputWrapper='mb-3'
          maxLength={50}
        />
        <Input
          required
          value={watch('patientCodePattern')}
          name={'patientCodePattern'}
          data-testid={'Subject Code Pattern'}
          errors={errors.patientCodePattern?.message}
          onChange={e =>
            onChange('patientCodePattern', e.target.value?.toUpperCase())
          }
          onKeyUp={e => {
            if (!patientCodePattern.test(e.key ?? '')) {
              e.preventDefault();
            }
          }}
          label={'Subject Code Pattern'}
          placeholder={'Subject Code Pattern'}
          classNameInputWrapper='mb-3'
          extraHelper={<Helper icon={AttentionIcon} />}
        />
        <Input
          required
          value={watch('numberOfPatients')}
          name={'numberOfPatients'}
          data-testid={'numberOfPatients'}
          errors={errors.numberOfPatients?.message}
          onChange={e => onChange('numberOfPatients', e.target.value)}
          onKeyUp={e => onKeyPress(e, watch('numberOfPatients')?.toString())}
          label={'Expected Number of Subjects'}
          placeholder={'Expected Number of Subjects'}
          classNameInputWrapper='mb-3'
          maxLength={5}
          className='[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
          type='number'
        />
        <Switch
          checked={watch('unscheduledAllowed')}
          onChange={e => onChange('unscheduledAllowed', e)}
          name={'unscheduledAllowed'}
          data-testid={'unscheduledAllowed'}
          label='Unscheduled Visits'
          extraLabel='Allow'
          classNameInputWrapper='flex justify-between items-center my-2 h-[3.2rem]'
        />
        <Switch
          checked={watch('fetchPatientGender')}
          onChange={e => onChange('fetchPatientGender', e)}
          name={'fetchPatientGender'}
          data-testid={'fetchPatientGender'}
          label=''
          extraLabel='Subject Gender Selection'
          classNameInputWrapper='flex justify-between items-center my-2 h-[3.2rem]'
        />
      </form>
      <footer className='mt-auto'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button
            data-testid={'cancel-btn'}
            className='w-[17rem]'
            onClick={onCloseDrawer}
          >
            Cancel
          </Button>
          <Button
            data-testid={'ok-btn'}
            className='w-[17rem]'
            type='primary'
            disabled={!isValid || isSubmitting || !isDirty || loading}
            onClick={cohortMode === 'EDIT_COHORT' ? update : submit}
          >
            {cohort ? 'Save' : 'Add'}
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        open={askQuit}
        onConfirm={() => close && close()}
        onCancel={() => setAskQuit(false)}
      />
    </Drawer>
  );
};
